import { ethers } from 'ethers';

function isSapientSigner(signer) {
  return signer.requestSignature !== undefined && signer.notifyStatusChange !== undefined;
}

class SignerWrapper {
  constructor(signer, eoa = true) {
    this.signer = signer;
    this.eoa = eoa;
  }
  getAddress() {
    return this.signer.getAddress();
  }
  async requestSignature(_id, message, _metadata, callbacks) {
    callbacks.onSignature(await this.signer.signMessage(message));
    return true;
  }
  notifyStatusChange(_i, _s, _m) {}
  suffix() {
    return [2];
  }
}

var index = /*#__PURE__*/Object.freeze({
  __proto__: null,
  isSapientSigner: isSapientSigner,
  SignerWrapper: SignerWrapper
});

function isSignerStatusRejected(status) {
  return status.rejected;
}
function isSignerStatusSigned(status) {
  return status.signature !== undefined;
}
function isSignerStatusPending(status) {
  return !isSignerStatusRejected(status) && !isSignerStatusSigned(status);
}
const InitialSituation = "Initial";

/**
 * It orchestrates the signing of a single digest by multiple signers.
 * It can provide internal visibility of the signing process, and it also
 * provides the internal signers with additional information about the
 * message being signed.
 */
class Orchestrator {
  constructor(signers, tag = Orchestrator.randomTag()) {
    this.observers = [];
    this.signers = [];
    this.count = 0;
    this.tag = tag;
    this.setSigners(signers);
  }
  static randomTag() {
    return `default-${ethers.utils.hexlify(ethers.utils.randomBytes(8)).slice(2)}`;
  }
  pullId() {
    return `${this.tag}-${this.count++}`;
  }
  setSigners(signers) {
    this.signers = signers.map(s => isSapientSigner(s) ? s : new SignerWrapper(s));
  }
  async getSigners() {
    return Promise.all(this.signers.map(async function (s) {
      return s.getAddress();
    }));
  }
  subscribe(observer) {
    this.observers.push(observer);
    return () => {
      this.observers = this.observers.filter(o => o !== observer);
    };
  }
  async notifyObservers(id, status, metadata) {
    await Promise.all([...this.signers.map(async function (signer) {
      return signer.notifyStatusChange(id, status, metadata);
    }), ...this.observers.map(async function (observer) {
      return observer(status, metadata);
    })]);
  }
  signMessage(args) {
    var _this = this;
    const id = this.pullId();
    return new Promise(async function (resolve) {
      const {
        message,
        metadata,
        callback,
        candidates
      } = args;
      const status = {
        ended: false,
        message,
        signers: {}
      };
      let lastMetadata = metadata != null ? metadata : {};
      const onNewMetadata = newMetadata => {
        lastMetadata = newMetadata;
        _this.notifyObservers(id, status, lastMetadata);
      };
      const onStatusUpdate = () => {
        try {
          _this.notifyObservers(id, status, lastMetadata);
          const pending = Object.entries(status.signers).filter(([_, s]) => isSignerStatusPending(s));
          if (callback && callback(status, onNewMetadata) || pending.length === 0) {
            status.ended = true;
            resolve(status);
            _this.notifyObservers(id, status, lastMetadata);
            return;
          }
        } catch (e) {
          console.error("Error while notifying observers", e);
        }
      };

      // we only call signers that are found in `candidates`
      // if `candidates` is undefined, we call all signers
      let signers = _this.signers;
      if (candidates) {
        const addresses = await Promise.all(_this.signers.map(async function (s) {
          return s.getAddress();
        }));
        signers = _this.signers.filter((_, i) => candidates.includes(addresses[i]));
      }

      // build callbacks object
      const accepted = await Promise.allSettled(signers.map(async function (s) {
        const saddr = await s.getAddress();
        status.signers[saddr] = {
          situation: InitialSituation
        };
        return s.requestSignature(id, message, metadata != null ? metadata : {}, {
          onSignature: signature => {
            const suffix = s.suffix();
            status.signers[saddr] = {
              signature,
              suffix
            };
            onStatusUpdate();
          },
          onRejection: error => {
            status.signers[saddr] = {
              rejected: true,
              error
            };
            onStatusUpdate();
          },
          onStatus: situation => {
            status.signers[saddr] = {
              situation
            };
            onStatusUpdate();
          }
        });
      }));
      for (let i = 0; i < accepted.length; i++) {
        const signer = _this.signers[i];
        const promise = accepted[i];
        if (promise.status === "rejected" || promise.value === false) {
          const prejected = promise;
          console.warn(`Signer ${await signer.getAddress()} rejected the request ${prejected.reason}`);
          status.signers[await signer.getAddress()] = {
            rejected: true,
            error: prejected.reason.toString()
          };
        }
      }
      onStatusUpdate();
    });
  }
}

export { InitialSituation, Orchestrator, isSignerStatusPending, isSignerStatusRejected, isSignerStatusSigned, index as signers };
